import React, {
  type PropsWithChildren,
  PureComponent,
  type ReactNode,
  type RefObject,
} from 'react';

import { cloneDeep, mergeWith, noop, omit, pick, unset } from 'lodash-es';

import { getOS } from '@iconik-io/core/src/os/get_os';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import { targetElement } from '../../utils/event_target';
import { ui } from '../../utils/helpers';
import {
  type FieldProps,
  FormContextProvider,
  type FormFieldValue,
  type ModelType,
  type WrappedComponentProps,
} from './form_context/form_context';
import type { FormErrors, FormFieldEvent } from './types';
import { getRequiredError } from './utils/get_required_error';
import { getValidationErrors } from './utils/get_validation_errors';
import validations from './validations/validations';

type Props<M extends object = ModelType> = PropsWithChildren<{
  onValidSubmit: (model: M, reset: (model?: Partial<M>) => void) => void;
  onInvalidSubmit?: () => void;
  // `name` is the field's dotted path into the model, not a key of it.
  onChange: <V>(model: M, name: string | undefined, value: V) => void;
  setModel: <V, CP>(
    model: Partial<M>,
    value: V,
    fieldProps: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ) => Partial<M>;
  disabled?: boolean;
  noValidate?: boolean;
  className?: string;
  id?: string;
  vertical?: boolean;
  errors?: FormErrors;
  isStep?: boolean;
  formRef?: RefObject<HTMLFormElement | null>;

  // This will define how initial values are stored in the model.
  // The default prop will set only a partial ignoring empty values: "", {}, [], null, undefined
  initialValuesSetter: <V>(value: V) => boolean;

  // If false onChange will be called only on user initiated change. In other words runs onChange only if
  // `event` exists for this action.
  // If true onChange will be called on any changes including side effect changes. Useful when you need
  // to react to user changes and changes coming from `value` prop updates of underlying
  // form field components.
  updateOnSideEffect?: boolean;
  submitByCommand?: boolean;

  uiKey?: string;
  aria?: AriaProps;

  shouldSendOnlyUpdatedFields?: boolean;
  tabIndex?: number;
  isSaveDisabled?: boolean;

  // Predicate deciding, per field name, whether to remove that field's value
  // from the model when it unmounts. Unset by default so forms that mount/
  // unmount fields conditionally keep unsaved edits; the search filters panel
  // opts in for its metadata fields only, so a filter from a hidden view can't
  // be resubmitted while other conditional fields keep their values.
  unregisterFieldOnUnmount?: (name: string) => boolean;
}>;

type State<M extends object = ModelType> = {
  isReset: boolean;
  resetValues: Partial<M>;
  isFormSubmitted: boolean;
};

/**
 * The form builds its model one mounted field at a time, so internally it can
 * only ever hold a partial. Callers declare the model type they expect and read
 * it as complete. Making that partial-ness visible is a change to every form in
 * the app, so it is stated once here instead.
 * https://linear.app/iconik/issue/FE-7732
 */
// biome-ignore lint/plugin: see above.
const asSubmittedModel = <M extends object>(model: Partial<M>): M => model as M;

function setModel<M extends object, V>(
  model: Partial<M>,
  value: V,
  fieldProps: WrappedComponentProps<V>,
): Partial<M> {
  // fields grouping
  // e.g:
  // from 'auth.login.password'
  // to: {auth: { login: { password: "admin" } } }
  const array = fieldProps.name ? fieldProps.name.split('.') : [];
  const object: Partial<M> = array.reduceRight<any>(
    (val, key) => ({ [key]: val }),
    value,
  );

  return mergeWith(
    cloneDeep(model),
    object,
    (_objValue, srcValue, key, obj) => {
      if (srcValue === undefined) {
        // A hack to process undefined values
        obj[key] = undefined;
      }
      if (srcValue === value) {
        return value;
      }
      return undefined;
    },
  );
}

// Keyed by the field's `name`, which is a dotted path into the model
// ("auth.login.password"), not a key of the model itself.
type Flags = Record<string, boolean | undefined>;

class Form<M extends object = ModelType> extends PureComponent<
  Props<M>,
  State<M>
> {
  static defaultProps = {
    onValidSubmit(): void {},
    onChange(): void {},
    errors: {},
    setModel,
    noValidate: true,
    disabled: false,
    initialValuesSetter: (value: FormFieldValue | undefined): boolean =>
      validations.required({}, value),
    submitByCommand: false,
    shouldSendOnlyUpdatedFields: false,
    tabIndex: undefined,
  };

  formRef = React.createRef<HTMLFormElement>();

  state: State<M> = {
    isReset: false,
    resetValues: {},
    isFormSubmitted: false,
  };

  model: Partial<M> = {};

  valid: Flags = {};

  updated: Flags = {};

  constructor(props: Props<M>) {
    super(props);
    if (props.formRef) {
      this.formRef = props.formRef;
    }
  }

  componentDidMount(): void {
    if (this.props.submitByCommand) {
      document.addEventListener('keydown', this.keydown);
    }
  }

  componentDidUpdate() {
    if (this.props.formRef && this.formRef !== this.props.formRef) {
      this.formRef = this.props.formRef;
    }
  }

  componentWillUnmount(): void {
    if (this.props.submitByCommand) {
      document.removeEventListener('keydown', this.keydown);
    }
  }

  keydown = (event: KeyboardEvent): void => {
    if (!this.props.disabled && !this.props.isSaveDisabled) {
      const isHoldingShiftAltKey = event.shiftKey || event.altKey;
      const holdingOnlyCtrl =
        (event.metaKey &&
          getOS() === 'MacOS' &&
          !event.ctrlKey &&
          !isHoldingShiftAltKey) ||
        (event.ctrlKey &&
          getOS() !== 'MacOS' &&
          !event.metaKey &&
          !isHoldingShiftAltKey);
      const isEnter = event.key === 'Enter';
      if (isEnter && targetElement(event.target)?.tagName !== 'TEXTAREA') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (isEnter && holdingOnlyCtrl) {
        this.validateSubmit();
      }
    }
  };

  updateModel = <V, CP>(
    value: V,
    fieldProps: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ): void => {
    const { name } = fieldProps;
    // A field without a name is not in the model, so there is nothing to track.
    if (name) {
      this.updated[name] = true;
    }
    this.model = this.props.setModel(this.model, value, fieldProps, event);

    if (this.props.updateOnSideEffect || event) {
      // if there is event means that is the user who updated the field
      // only call onChange if the user updated the field
      this.props.onChange(asSubmittedModel(this.model), name, value);
    }
  };

  unsetValue = <V, CP>(fieldProps: FieldProps<V, CP>): void => {
    const name = fieldProps.name;
    if (!name || !this.props.unregisterFieldOnUnmount?.(name)) {
      return;
    }
    // `unset` removes the field's branch from the model along its dot-path name.
    const model = cloneDeep(this.model);
    unset(model, name);
    this.model = model;
    Reflect.deleteProperty(this.updated, name);
    Reflect.deleteProperty(this.valid, name);
  };

  setInitialValue = <V, CP>(value: V, fieldProps: FieldProps<V, CP>): void => {
    // set initial value if there is any value to set
    const initialValuesSetter = this.props.initialValuesSetter(value);
    if (initialValuesSetter) {
      this.model = this.props.setModel(this.model, value, fieldProps);
    }
    const { name } = fieldProps;
    // clear the value in the model if stored
    // (common for mounted and unmounted fields with a cleared value)
    if (name && this.updated[name] && !initialValuesSetter) {
      this.model = this.props.setModel(this.model, value, fieldProps);
    }
  };

  validateField = <V, CP>(
    value: V,
    props: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ): string[] | null => {
    let errors: string[] = [];
    // Don't validate untouched autofocus fields on blur event
    const { name } = props;
    if (
      event &&
      event.type === 'blur' &&
      props?.autoFocus &&
      name &&
      !this.updated[name] &&
      this.valid[name] === undefined
    ) {
      return null;
    }
    if (errors.length === 0) {
      errors = getValidationErrors(
        this.model,
        value,
        props.validations,
        props.validationMessages,
      );
    }
    const required = getRequiredError(this.model, value, props.required);
    if (required) {
      errors = [required];
    }
    if (name) {
      this.valid[name] = errors.length === 0;
    }
    return errors.length === 0 ? null : errors;
  };

  reset = (model: Partial<M> = {}): void => {
    const newData = { ...model };
    this.setState({ isReset: false }, () => {
      this.setState({ isReset: true, resetValues: newData });
    });
    if (model) {
      this.model = newData;
    }
  };

  onSubmit = (event: FormFieldEvent): void => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.valid = {};
    this.validateSubmit();
  };

  validateSubmit = (): boolean => {
    let isValid = false;
    // isFormSubmitted change triggers all fields validation via
    // componentDidUpdate in every form_field component. And all this
    // triggers validateField method of the current class for every field.
    this.setState({ isFormSubmitted: true }, () => {
      const errors = omit(this.props.errors ?? {}, Object.keys(this.updated));
      Object.keys(errors).forEach((key) => {
        this.valid[key] = false;
      });
      isValid = !Object.values(this.valid).includes(false);
      if (isValid) {
        if (this.props.shouldSendOnlyUpdatedFields) {
          // `updated` is keyed by dotted path, which is what pick reads.
          const modelWithUpdatesOnly: Partial<M> = pick(
            this.model,
            Object.keys(this.updated),
          );
          this.props.onValidSubmit(
            asSubmittedModel(modelWithUpdatesOnly),
            this.reset,
          );
        } else {
          this.props.onValidSubmit(
            asSubmittedModel({ ...this.model }),
            this.reset,
          );
        }
        // only clear updated if the form is not in a progress step,
        // so we keep track of the updated in previous steps
        if (!this.props.isStep) {
          this.updated = {};
        }
      } else {
        this.props.onInvalidSubmit?.();
        this.focusInvalidInput();
      }
      // reset isFormSubmitted so fields are validated on the next submit
      this.setState({ isFormSubmitted: false });
    });
    return isValid;
  };

  focusInvalidInput(): void {
    let invalidField: string | undefined;
    for (const [name, value] of Object.entries(this.valid)) {
      if (!invalidField && value === false) {
        invalidField = name;
      }
    }
    if (this.formRef.current) {
      const field: HTMLElement | null = this.formRef.current.querySelector(
        `[data-ui-input-name="${invalidField}"]`,
      );
      if (field && field.focus) {
        field.focus();
        field.scrollIntoView?.({ behavior: 'smooth' });
      }
    }
  }

  render(): ReactNode {
    const {
      disabled,
      children,
      vertical,
      id,
      errors,
      className,
      noValidate,
      uiKey,
      aria,
      tabIndex,
    } = this.props;

    return (
      <form
        id={id}
        className={className}
        onSubmit={this.onSubmit}
        ref={this.formRef}
        noValidate={noValidate}
        tabIndex={tabIndex}
        {...ui(uiKey)}
        {...ariaToReactProps(aria)}
      >
        <FormContextProvider
          value={{
            setValue: this.updateModel,
            setInitialValue: this.setInitialValue,
            unsetValue: this.props.unregisterFieldOnUnmount
              ? this.unsetValue
              : noop,
            validate: this.validateField,
            errors,
            isFormSubmitted: this.state.isFormSubmitted,
            isFormDisabled: disabled,
            isReset: this.state.isReset,
            resetValues: this.state.resetValues,
            isVertical: Boolean(vertical),
          }}
        >
          {children}
        </FormContextProvider>
      </form>
    );
  }
}

export default Form;
